import { useState, useRef, useEffect } from 'react';
import { Info } from 'lucide-react';
import { TONE_EXAMPLES, type ToneValue } from '../../shared/toneInstructions';

interface MessagingStyleTooltipProps {
  agentType: 'whatsapp' | 'email' | 'linkedin' | 'call';
}

const TONE_LABELS: Record<ToneValue, string> = {
  friendly_casual: 'Friendly & Casual',
  professional: 'Professional',
  direct: 'Direct',
};

export default function MessagingStyleTooltip({ agentType }: MessagingStyleTooltipProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const examples = TONE_EXAMPLES[agentType];
  if (!examples) return null;

  return (
    <div className="relative inline-block" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="ml-1.5 text-gray-400 hover:text-gray-600 transition-colors"
      >
        <Info className="w-3.5 h-3.5" />
      </button>

      {open && (
        <div className="absolute z-50 left-0 top-full mt-2 w-80 bg-white border border-gray-200 rounded-lg shadow-lg p-4 text-left">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">
            Example messages by style
          </p>
          <div className="space-y-3">
            {(Object.keys(TONE_LABELS) as ToneValue[]).map((tone) => (
              <div key={tone}>
                <p className="text-xs font-semibold text-gray-700 mb-1">
                  {TONE_LABELS[tone]}
                </p>
                <p className="text-xs text-gray-500 leading-relaxed italic">
                  "{examples[tone]}"
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
